import { format, isSameDay, parse, parseISO } from "date-fns";
import { analyticEvents as triggerAnalyticEvent } from "@actions/analyticEvents";
import { updateNormalisedConfig } from "@store/slices/config";
import { store } from "@store/store";
import getParameterByName from "./queryParams";
import Router from "next/router";
import { REQUEST_PAYLOAD } from "./constants";
import { getFromSessionStorage } from "@actions/getFromSessionStorage";
import actions from "@actions/actionsMap";
import { isWebView } from "./commonUtils";

export const DASHCAM_ADDRESS_NODES = [
  "select_dashcam_address_node",
  "select_car_address_node"
];

export const currentYear = new Date().getFullYear();
export const registrationMaskedValue = currentYear - 16;

export const formatDate = (date: string) => {
  // todo: remove date as param as not-used
  console.log(date);
  return new Date().toLocaleString();
};

export const isDateToday = (dateString: string) => {
  try {
    const date = parseISO(dateString);
    return isSameDay(date, new Date());
  } catch {
    return false;
  }
};

export const formatDayLabel = (dateString: string) => {
  if (isDateToday(dateString)) {
    return "TODAY";
  }
  try {
    const date = parseISO(dateString);
    return format(date, "EEE").toUpperCase();
  } catch {
    return "";
  }
};

export const formatDateDayNumber = (dateString: string) => {
  try {
    const date = parseISO(dateString);
    return format(date, "dd");
  } catch {
    return "";
  }
};

/** Format ISO date string to "d MMMM" (e.g. "24 February") */
export const formatDateFromISO = (dateString: string) => {
  if (!dateString) return "";
  try {
    const date = parseISO(dateString);
    return format(date, "d MMMM");
  } catch {
    return dateString;
  }
};

/** Format dd-MM-yyyy date string to "d MMMM" (e.g. "24 February") */
export const formatDateFromDDMMYYYY = (dateString: string) => {
  if (!dateString) return "";
  try {
    const date = parse(dateString, "dd-MM-yyyy", new Date());
    return format(date, "d MMMM");
  } catch {
    return dateString;
  }
};

/** Format time slot string (removes :00, normalizes whitespace, ensures space before AM/PM) */
export const formatTimeSlot = (slot: string) => {
  if (!slot) return "";
  return slot
    .replace(/:00/g, "")
    .replace(/\s+/g, " ")
    .replace(/\s(AM|PM)/gi, " $1")
    .trim();
};

export const getWidgetSpecificIsVisible = (
  normalisedConfig: any,
  id: string
): boolean => {
  const { props } = normalisedConfig[id] || {};
  return props?.is_visible;
};

export const getWidgetSpecificProp = (
  normalisedConfig: any,
  id: string,
  propKey: string
): any => {
  const { props } = normalisedConfig[id] || {};
  return props?.[propKey];
};

export const getWidgetSpecificProps = (
  normalisedConfig: any,
  id: string
): any => {
  const { props } = normalisedConfig[id] || {};
  return props;
};

export const getErrorMessageForId = (normalisedConfig: any, id: string) => {
  const { props } = normalisedConfig?.[id] || {};
  return props?.error_message;
};

export function capitalizeFirstLetter(string: string): string {
  if (typeof string !== "string" || string?.length === 0) {
    return "";
  }
  if (string === "cng") return string?.toLocaleUpperCase();
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export const updateStateBasedOnFieldChange = (parentId: string) => {
  const { configuration } = store.getState() || {};
  const { normalisedConfig } = configuration || {};
  const updatedNormalisedConfig = JSON.parse(JSON.stringify(normalisedConfig));
  const data = updatedNormalisedConfig[parentId];

  data.props.error_message = "";
  store.dispatch(
    updateNormalisedConfig({
      normalisedConfig: updatedNormalisedConfig
    })
  );
};

export const transformMakeData = (
  obj: Record<string, string>
): { name: string; link: string }[] => {
  return Object.entries(obj).map(([name, link]) => ({ name, link }));
};

export function getValueFromObject(sharedObj: any, payload: any) {
  if (!payload || !sharedObj) return;
  const key = payload?.name;
  return sharedObj?.[key] || null;
}

// marking this as const so that its readonly and cannot be changed
export const monthMap = {
  Jan: 1,
  Feb: 2,
  Mar: 3,
  Apr: 4,
  May: 5,
  Jun: 6,
  Jul: 7,
  Aug: 8,
  Sep: 9,
  Oct: 10,
  Nov: 11,
  Dec: 12
} as const;

export const monthToNumber = (month: keyof typeof monthMap): number | null => {
  return monthMap[month] || null;
};

export const numberToMonth = (number) => {
  return (
    Object.entries(monthMap).find(([_, num]) => num === number)?.[0] || null
  );
};

export const transformYearOptions = (data: string[]) => {
  return data.map((year, index) => {
    const yearValue = parseInt(year, 10);
    if (isNaN(yearValue)) {
      return {
        name: year,
        value: "0",
        is_registration_year_masked: true,
        maskedValue: Number(data?.[index - 1]) - 1 || registrationMaskedValue,
        className: "registrationYear"
      };
    } else {
      return {
        name: yearValue,
        value: yearValue,
        className: "registrationYear"
      };
    }
  });
};

export enum ADDONS_CAR {
  PA_COVER = "pa_owner_car",
  RSA_OUTSTATION = "rsa_outstation_keyprotect_combo_car",
  RSA_CAR = "rsa_car"
}

export const ADDONS_WITH_VARIANTS = ["RSA_OUTSTATION", "RSA_CAR"];

export const getAddonDescription = (description) => {
  let finalDesc = description
    ?.replace(/{bold}/g, "<b>")
    .replace(
      /{ulist}/g,
      `<ul style="list-style-type: disc; list-style-position: inside;">`
    )
    .replace(/{litem}/g, `<li><span style="position: relative; left: -6px;" >`)
    .replace(/\n/g, "<br/>")
    .replace(/{\/bold}/g, "</b>")
    .replace(/{\/litem}/g, `</span></li>`)
    .replace(/{\/ulist}/g, "</ul>");
  return finalDesc;
};

export const ROUND_TO_1_DECIMAL = "roundto1decimal";

export const IdvOPTIONS = { min: "minimum", max: "maximum", custom: "custom" };

export const IS_INFO_TEXT = "infotext";

export const ADDONS_CONTAINER = "ADDONS";

export const CONSUMABLES = "consumables";

export const MINIFIED_DETAILS = "minifiedDetails";

export const PREMIUM_DETAILS = "premium-details";

export const EXPIRED_POLICY_STATUS_90_DAYS = "90-";

export const EDIT_MMV_DETAILS_NODE = "edit_mmv_details_node";

export const ENTER_MMV_DETAILS_NODE = "enter_mmv_details_node";

export const scrollIntoViewWrapper = (elementId, blockType = "center") => {
  const target = document?.getElementById(elementId);
  if (!target) return;
  target?.scrollIntoView({
    behavior: "smooth",
    //@ts-ignore
    block: blockType,
    inline: "center"
  });
};

export const getDataFromStore = (key: string) => {
  try {
    return store?.getState()?.globalState?.payloadData?.[key];
  } catch (err) {
    console.log("Error in accessing store ", err);
  }
};
export const getNcb = (ncb) => {
  let displayValue;
  let value;

  ncb = ncb?.toString();

  if (ncb?.includes("%")) {
    displayValue = ncb;
    value = parseInt(ncb?.split("%")[0]);
  } else {
    displayValue = ncb + "%";
    value = parseInt(ncb);
  }

  return {
    displayValue,
    value
  };
};

/** 
 * Function to send the segment event for client side specific events
@param actName: action (view || action)
@param actType: actionType (tapBtn || view_**)
@param ename: Event Name 
*/
// TODO: FORMAT THIS FXN  to meaningful name and usage
export const sampleAnalytics = (actName, actType, eName, restArgs = {}) => {
  if (!actName || !actType || !eName) return;
  let edata;
  if (actName === "view") {
    edata = {
      view_name: eName,
      view_type: actType,
      ...restArgs
    };
  } else {
    edata = {
      action_name: eName,
      action_type: actType,
      ...restArgs
    };
  }
  if (typeof triggerAnalyticEvent === "function") {
    triggerAnalyticEvent({
      edata,
      odata: {
        journey: getJourneyType()
      },
      event_name: actName
    });
  }
};

export const desktopRightContainerWidth = "550px";

export const DONT_SHOW_CUSTOM_TEXT = "DONT_SHOW_CUSTOM_TEXT";

export const REVIEW_EDIT_NODE = "review_edit_node";

export const StringToNumberPrice = (priceInString) => {
  return Number(priceInString?.replace(/,/g, ""));
};

export const checkIfErrorNode = (data) => {
  if (!data) return false;
  return data?.current_node === "error_node";
};

export const capitalizeDisplayName = (displayName) => {
  if (typeof displayName !== "string") {
    return displayName;
  }

  return displayName
    .split(" ")
    .map((word) =>
      word ? word?.charAt(0)?.toUpperCase() + word?.slice(1) : ""
    )
    .join(" ");
};
export const checkIsPartnerFlow = () => {
  return getParameterByName("utm_source") == "partnership";
};

export const compressPlanName = (plan: any) => {
  let planName = plan;

  if (planName) {
    planName = planName?.replace(/comprehensive/g, "c");
    planName = planName?.replace(/zero_dep/g, "zd");
    planName = planName?.replace(/deductible/g, "d");
  }

  return planName;
};

export const getJourneyType = () => {
  const { product } = Router.query;
  if (Router.asPath?.includes("/auto-storefront/new-car")) {
    return "new_car";
  }
  if (Router.asPath?.includes("/auto-storefront/dashcam")) {
    return "dashcam";
  }
  if (Router.asPath?.includes("/auto-storefront/fresh-car")) {
    return "fresh_car";
  }
  return product === "car" ? "renewal_car" : "renewal_bike";
};

export const checkIfRenewalJourney = () => {
  if (Router.asPath?.includes("/auto-storefront/renew")) {
    return true;
  }
  return false;
};

export const getRequestpayload = () => {
  const { product } = Router.query;
  const journey = getJourneyType();

  const requestPayload = REQUEST_PAYLOAD[journey];

  return {
    ...requestPayload,
    data: {
      ...requestPayload.data,
      ...(product && { product })
    }
  };
};

const sessionKeys = {
  vehicle_addons_node_visited: false,
  family_addons_node_visited: false,
  idv_packs_node_visited: false,
  multi_year_plan_node_visited: false,
  multi_tenure_plan_node_visited: false,
  is_edit_journey: false,
  group_addons_node_visited: false
};

export const setSessionVariablesToCookies = () => {
  let keys = Object.keys(sessionKeys);
  const sessionData = {};
  if (keys.length === 0) {
    return sessionData;
  }

  keys.forEach((key) => {
    const value = getFromSessionStorage({ key: key });
    if (value !== null && value !== undefined) {
      sessionData[key] = JSON.parse(value);
    }
    else {
      sessionData[key] = sessionKeys[key];
    }
  });
  return sessionData;
};

export const REVIEW_NODES = new Set([
  "tp_review_node",
  "comparison_review_node",
  "od_comp_review_node",
  "checkout_node"
]);

export const RESET_TRIGGER_NODES = new Set([
  "vehicle_addons_node",
  "family_addons_node",
  "idv_packs_node",
  "group_addons_node",
  "multi_year_plan_node",
  "multi_tenure_plan_node"
]);

export function shouldResetEditJourney(currentNode: string, previousNode?: string): boolean {
  return (
    REVIEW_NODES.has(currentNode) &&
    !!previousNode &&
    RESET_TRIGGER_NODES.has(previousNode)
  );
}

/**
 * Generic function to execute actions by their type names
 * @param actionsConfig - Array of action configurations with `type` and `args`
 * @param additionalArgs - Optional additional arguments to merge with each action's args
 * @returns void
 */
export const executeActions = (actionsConfig: Array<any> = [], additionalArgs: any = {}) => {
  if (!Array.isArray(actionsConfig) || actionsConfig.length === 0) {
    return;
  }

  actionsConfig.forEach((actionConfig: any) => {
    // Support both new format (type) and legacy format (funCall)
    if (actionConfig?.type) {
      const actionFunction = actions[actionConfig.type];
      if (typeof actionFunction === "function") {
        actionFunction({
          ...(actionConfig.args || {}),
          ...additionalArgs
        });
      } else {
        console.warn(`Action type "${actionConfig.type}" not found in actionsMap`);
      }
    } else if (actionConfig?.funCall && typeof actionConfig.funCall === "function") {
      // Legacy support for funCall
      actionConfig.funCall({
        ...(actionConfig.args || {}),
        ...additionalArgs
      });
    }
  });
};

export const toReadableList = (
  items: any[],
  formatter: (item: any) => string = item => item
): string => {
  if (!Array.isArray(items) || items.length === 0) return "";

  const size = items.length;
  return items
    .map((item, index) => {
      const value = formatter(item);
      if (index === size - 2) return `${value} and `;
      if (index === size - 1) return `${value}`;
      return `${value}, `;
    })
    .join("");
};

export function trimLeadingZerosInFloat(num: number) {
  let str = String(num);
  str = str.replace(/^0+(?=\d)/, '');
  return parseFloat(str);
}
/**
 * Removes underscores and capitalizes the first letter of each word
 * @param str - String with underscores (e.g., "fuel_type" or "model_name")
 * @returns Formatted string (e.g., "Fuel Type" or "Model Name")
 */
export const removeUnderscoreAndCapitalize = (str: string): string => {
  if (typeof str !== "string" || str.length === 0) {
    return "";
  }
  return str
    .split("_")
    .map((word) => {
      if (word.length === 0) return "";
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
};

export function lowercaseFirstLetter(str) {
  if (!str) return str;
  return str.charAt(0).toLowerCase() + str.slice(1);
}

export const checkIfRenewalPolicyPath = () => {
  if (typeof window !== "undefined") {
    const path = window.location.pathname;
    return path.includes("/gi/auto-storefront/renew-policy/");
  }
  return false;
};

const getProposalIdFromRoute = () => {
  const routerProposalId = Router.query?.proposal_id || Router.query?.proposal_ekey;

  if (typeof routerProposalId === "string") {
    return routerProposalId;
  }

  if (typeof window === "undefined") {
    return "";
  }

  const searchParams = new URLSearchParams(window.location.search);
  return (
    searchParams.get("proposal_id") || searchParams.get("proposal_ekey") || ""
  );
};

const getPathToRedirectAfterLogin = (redirectPath?: string) => {
  if (redirectPath) {
    return redirectPath;
  }

  if (typeof window === "undefined") {
    return "";
  }

  return window.location.pathname + window.location.search;
};

const hostUrl = (process.env.HOST_URL || "").replace(/\/$/, "");

const buildAbsoluteRedirectUrl = (redirectPath: string) => {
  return new URL(redirectPath, hostUrl).toString();
};

const getProposalPhoneFromRoute = async (): Promise<string | undefined> => {
  const proposalId = getProposalIdFromRoute();

  if (!proposalId) {
    return undefined;
  }

  try {
    const { getProposalDetails } = await import("@service/apiServices");
    const proposalDetails = await getProposalDetails(proposalId);
    return proposalDetails?.user?.phone;
  } catch (error) {
    console.error("Unable to fetch proposal phone number:", error);
    return undefined;
  }
};

export const redirectToLogin = async (
  redirectPath?: string,
  phone?: string
): Promise<void> => {
  if (typeof window === "undefined") {
    return;
  }

  const pathToRedirect = getPathToRedirectAfterLogin(redirectPath);
  const proposalPhone = await getProposalPhoneFromRoute();

  if (isWebView() && window?.flutter_inappwebview?.callHandler) {
    const { appWebViewAuthFlow } = await import("@actions/appWebViewAuthFlow");

    appWebViewAuthFlow({
      phone: proposalPhone || phone,
      nextDeeplink: buildAbsoluteRedirectUrl(pathToRedirect)
    });
    return;
  }

  const encodedPath = encodeURIComponent(pathToRedirect);
  const signInUrl = proposalPhone
    ? `${hostUrl}/authn/v1/signin?mobile=${proposalPhone}&disable=true&state=${encodedPath}`
    : `${hostUrl}/authn/v1/signin?state=${encodedPath}`;
  window.location.href = signInUrl;
};

export const doesLoggedInUserMatchProposal = async (
  loginStatus: any,
  proposalId?: string
): Promise<boolean> => {
  if (!loginStatus?.isLoggedIn || !proposalId) {
    return true;
  }

  const { getProposalDetails } = await import("@service/apiServices");
  const proposalDetails = await getProposalDetails(proposalId);

  const loggedInUserPhone = loginStatus?.userData?.phone;
  const proposalUserPhone = proposalDetails?.user?.phone;

  // Only block access on a confirmed mismatch because some responses omit phone fields.
  if (!loggedInUserPhone || !proposalUserPhone) {
    return true;
  }

  return loggedInUserPhone === proposalUserPhone;
};

export const redirectToLoginIfNotLoggedIn = async (redirectPath?: string): Promise<boolean> => {
  const { checkUserLoginStatus } = await import("@service/apiServices");
  
  try {
    const loginStatus = await checkUserLoginStatus();
    const proposalId = getProposalIdFromRoute();

    if (!loginStatus.isLoggedIn) {
      await redirectToLogin(redirectPath);
      return false;
    }

    if (proposalId) {
      try {
        const doesPhoneMatch = await doesLoggedInUserMatchProposal(
          loginStatus,
          proposalId
        );

        if (!doesPhoneMatch) {
          await redirectToLogin(redirectPath);
          return false;
        }
      } catch (proposalError) {
        console.error("Unable to verify proposal phone number:", proposalError);
      }
    }

    return true;
  } catch (error) {
    console.error("Error checking login status:", error);
    await redirectToLogin(redirectPath);
    return false;
  }
};
